import React, { useState } from "react";
import { ComplianceScore } from "../../types/security";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  AlertCircle,
  Info,
  TrendingUp,
  Calendar,
  FileText,
  Shield,
} from "lucide-react";

interface ComplianceOverviewProps {
  scores: ComplianceScore[];
}

const getScoreColor = (score: number): string => {
  if (score >= 90) return "#22c55e"; // green
  if (score >= 75) return "#3b82f6"; // blue
  if (score >= 60) return "#f59e0b"; // orange
  return "#ef4444"; // red
};

const getScoreColorClass = (score: number): string => {
  if (score >= 90) return "text-green-600";
  if (score >= 75) return "text-blue-600";
  if (score >= 60) return "text-orange-600";
  return "text-red-600";
};

const CircularProgress: React.FC<{ score: number; size?: number }> = ({ score, size = 80 }) => {
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  const color = getScoreColor(score);

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-lg font-bold ${getScoreColorClass(score)}`}>{score}%</span>
      </div>
    </div>
  );
};

export const ComplianceOverview: React.FC<ComplianceOverviewProps> = ({ scores }) => {
  const [selectedCompliance, setSelectedCompliance] = useState<ComplianceScore | null>(null);

  // Calculate overall compliance score
  const overallScore = Math.round(
    scores.reduce((sum, score) => sum + score.score, 0) / scores.length
  );

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">AI Compliance Overview Score</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {scores.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedCompliance(item)}
                className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
              >
                <CircularProgress score={item.score} size={70} />
                <div className="mt-2 text-center">
                  <p className="text-xs font-medium text-gray-900 group-hover:text-primary transition-colors">
                    {item.shortName}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">
                    {item.description.split(" ").slice(0, 5).join(" ")}...
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Overall Score Summary */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Overall Compliance</p>
                <p className="text-base font-semibold mt-1">AI Governance & Risk Management</p>
              </div>
              <div className="flex items-center gap-4">
                <CircularProgress score={overallScore} size={60} />
                <div>
                  <p className={`text-3xl font-bold ${getScoreColorClass(overallScore)}`}>
                    {overallScore}%
                  </p>
                  <p className="text-xs text-muted-foreground">Average</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Detail Sheet */}
      <Sheet open={!!selectedCompliance} onOpenChange={() => setSelectedCompliance(null)}>
        <SheetContent className="w-[600px] sm:max-w-2xl overflow-y-auto">
          {selectedCompliance && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-3 mb-2">
                  <CircularProgress score={selectedCompliance.score} size={60} />
                  <div className="flex-1">
                    <SheetTitle className="text-base font-bold">
                      {selectedCompliance.name}
                    </SheetTitle>
                    <div className="text-sm text-muted-foreground">
                      {selectedCompliance.category.charAt(0).toUpperCase() +
                        selectedCompliance.category.slice(1)}{" "}
                      Framework
                    </div>
                  </div>
                </div>
                <SheetDescription className="text-sm">
                  {selectedCompliance.description}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-4 mt-4">
                {/* Status and Trend */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Status:</span>
                    <Badge
                      className={`${
                        selectedCompliance.score >= 75
                          ? "bg-green-100 text-green-800 hover:bg-green-100"
                          : selectedCompliance.score >= 60
                            ? "bg-orange-100 text-orange-800 hover:bg-orange-100"
                            : "bg-red-100 text-red-800 hover:bg-red-100"
                      }`}
                    >
                      {selectedCompliance.details.status.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                    <span className="text-green-600 font-medium">
                      {selectedCompliance.details.trendPercentage}
                    </span>
                    <span className="text-muted-foreground">this month</span>
                  </div>
                </div>

                {/* Control Gaps Priority */}
                <Card className="border-orange-200 bg-orange-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      Priority Control Gaps
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-xs px-2 py-0.5">
                          CRITICAL
                        </Badge>
                        <span className="text-sm text-muted-foreground">High Risk</span>
                      </div>
                      <span className="text-lg font-bold text-red-700">
                        {selectedCompliance.details.criticalGaps}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 text-xs px-2 py-0.5">
                          HIGH
                        </Badge>
                        <span className="text-sm text-muted-foreground">Medium Risk</span>
                      </div>
                      <span className="text-lg font-bold text-orange-700">
                        {selectedCompliance.details.highGaps}
                      </span>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total Gaps:</span>
                        <span className="font-medium">
                          {selectedCompliance.details.pending} controls
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Remediation Timeline */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      Remediation Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Target Compliance Date:</span>
                      <span className="font-medium">{selectedCompliance.details.nextDeadline}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Days Remaining:</span>
                      <span className="font-medium text-orange-600">77 days</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Estimated Effort:</span>
                      <span className="font-medium">
                        {selectedCompliance.details.estimatedCost}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Responsible Team:</span>
                      <span className="font-medium">
                        {selectedCompliance.details.responsibleTeam}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Documentation Status */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-600" />
                      Documentation Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Evidence Collected</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-600"
                            style={{ width: `${selectedCompliance.score}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{selectedCompliance.score}%</span>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Missing Documentation:</span>
                      <span className="font-medium text-orange-600">
                        {selectedCompliance.details.documentationGaps} items
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Next Audit:</span>
                      <span className="font-medium">{selectedCompliance.details.nextReview}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Priority Actions */}
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-600" />
                      Priority Actions Required
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedCompliance.details.priorityActions.map((action, index) => (
                        <div
                          key={action.id}
                          className="flex items-start gap-2 p-2 bg-white rounded border"
                        >
                          <Badge
                            className={`${
                              action.priority === "critical"
                                ? "bg-red-100 text-red-800 hover:bg-red-100"
                                : "bg-orange-100 text-orange-800 hover:bg-orange-100"
                            } text-xs px-2 py-0.5 mt-0.5`}
                          >
                            {index + 1}
                          </Badge>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{action.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {action.description} • Due in {action.dueInDays} days
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Related Security Issues */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Shield className="h-4 w-4 text-red-600" />
                      Related Security Issues
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Critical Issues:</span>
                      <span className="font-bold text-red-600">
                        {selectedCompliance.details.relatedIssues.critical}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">High Issues:</span>
                      <span className="font-bold text-orange-600">
                        {selectedCompliance.details.relatedIssues.high}
                      </span>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-2">
                      View All Related Issues
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
